const handlers = {
    arrowKeyLeft: (player, options, event) => {
        if (player.settings().enableSeeking) {
            //Skip Back 10 seconds
            player.currentTime(player.currentTime() - 10);
        }
    },
    arrowKeyRight: (player, options, event) => {
        if (player.settings().enableSeeking) {
            //Skip Forward 10 seconds
            player.currentTime(player.currentTime() + 10);
        }
    },
    audioKey: (player, options, event) => {
        const audioBtn = player.getDescendant('ControlBar', 'AudioDescriptionsButton');
        if (audioBtn) {
            audioBtn.pressButton();
        }
    },
    playbackKey: (player, options, event) => {
        const playbackBtn = player.getDescendant('ControlBar', 'PlaybackRateMenuButton');
        if (playbackBtn) {
            playbackBtn.pressButton();
        }
    },
    captionSettingsKey: (player, options, event) => {
        //TextTrackSettings is video.js's name for the Caption Settings modal
        const captionsModal = player.getChild("TextTrackSettings");
        if (captionsModal) {
            const captionsPlugin = player.captions();
            //visible instead of enabled due to enabled always being set to true on construction
            if (captionsPlugin.visible) {
                captionsModal.open();
            }
        }
    },
    swapStreamsKey: (player, options, event) => {
        const swapBtn = player.getDescendant('ControlBar', 'SwapStreamsButton');
        if (swapBtn && player.settings()?.enableSwapStreams) {
            swapBtn.options_.clickHandler.call(swapBtn, options, event);
        }
    },
    lockControlsKey: (player, options, event) => {
        const ctrlsLockBtn = player.getDescendant('ControlBar', 'ControlsLockButton');
        if (ctrlsLockBtn) {
            ctrlsLockBtn.options_.clickHandler.call(ctrlsLockBtn);
        }
    }
};

export const hotkeysConfig = {
    seekStep: 10,
    enableModifiersForNumbers: false,
    enableHoverScroll: true,
    playPauseKey: (event, player) => {
        return event.key === ' ' && noModifiers(event) && player.settings().enablePlayPause;
    },
    rewindKey: (event, player) => {
        return event.key === 'ArrowLeft' && noModifiers(event) && player.settings().enableSeeking;
    },
    forwardKey: (event, player) => {
        return event.key === 'ArrowRight' && noModifiers(event) && player.settings().enableSeeking;
    },
    fullscreenKey: (event, player) => {
        return ((event.key === 'f' && noModifiers(event)) || (event.key === ' ' && ctrlOnly(event))) && player.settings().enableFullScreen;
    },
    muteKey: (event, player) => {
        return event.key === 'm' || (event.key === 'm' && ctrlOnly(event));
    },
    //if any of these are updated, make sure the 'modifyControlText' function in the player is updated accordingly
    customKeys: {
        questionKey: setupCustomKey("q", "askAQuestion", "openModal"),
        toggleCaptionsKey: setupCustomKey("c", "captions", "toggleCaptions"),
        toggleChaptersKey: setupCustomKey("C", "chapters", "toggleChaptersButton"),
        searchKey: setupCustomKey("s", "search", "openModal"),
        infoKey: setupCustomKey("i", "metadata", "openModal"),
        shareKey: setupCustomKey("e", "share", "openModal"),
        helpKey: setupCustomKey("h", "help", "handleClick"),
        downloadKey: setupCustomKey("d", "downloads", "openModal"),
        pollsKey: setupCustomKey("p", "polls", "open"),
        nextPresentationKey: setupCustomKey(">", "playlist", "simulateNextBtnClick"),
        prevPresentationKey: setupCustomKey("<", "playlist", "simulatePrevBtnClick"),
        homePresentationKey: setupCustomKey("?", "playlist", "simulateHomeBtnClick"),
        questionKeyAlternate: setupCustomCtrlKey("?", "askAQuestion", "openModal"),  //Ctrl + Shift + Slash (Ctrl + Question Mark)
        toggleCaptionsKeyAlternate: setupCustomCtrlOnlyKey(";", "captions", "toggleCaptions"),  //Ctrl + Semicolon
        toggleChaptersKeyAlternate: setupCustomCtrlOnlyKey("'", "chapters", "toggleChaptersButton"),  //Ctrl + Apostrophe
        searchKeyAlternate: setupCustomCtrlOnlyKey("/", "search", "openModal"),  //Ctrl + Slash
        infoKeyAlternate: setupCustomKey(":", "metadata", "openModal"),  //Shift + Semicolon (Colon)
        shareKeyAlternate: setupCustomCtrlOnlyKey(",", "share", "openModal"),  //Ctrl + Comma
        helpKeyAlternate: setupCustomCtrlKey("V", "help", "handleClick"),  //Ctrl + Shift + V
        downloadKeyAlternate: setupCustomCtrlKey(":", "downloads", "openModal"),  //Ctrl + Shift + Semicolon (Ctrl + Colon)
        pollsKeyAlternate: setupCustomCtrlKey("\"", "polls", "open"),  //Ctrl + Shift + Apostrophe (Ctrl + Double Quote)
        nextPresentationKeyAlternate: setupCustomKey("}", "playlist", "simulateNextBtnClick"),  //Shift + Rt Bracket (Rt Curly Bracket)
        prevPresentationKeyAlternate: setupCustomKey("{", "playlist", "simulatePrevBtnClick"),  //Shift + Lt Bracket (Lt Curly Bracket)
        arrowKeyLeftAlternate: {
            key: event => (event.key === '[' && ctrlOnly(event)),  //Ctrl + Left Bracket
            handler: handlers.arrowKeyLeft
        },
        arrowKeyRightAlternate: {
            key: event => (event.key === ']' && ctrlOnly(event)),  //Ctrl + Right Bracket
            handler: handlers.arrowKeyRight
        },
        arrowKeyShift: {
            key: event => (event.key === 'ArrowRight' || event.key === 'ArrowLeft') && !event.ctrlKey && !event.altKey,
            handler: (player, options, event) => {
                if (player.settings().enableSeeking) {
                    //Skip Back 1 second
                    if (event.key === 'ArrowLeft' && event.shiftKey) {
                        player.currentTime(player.currentTime() - 1);
                    }

                    //Skip Forward 1 second
                    if (event.key === 'ArrowRight' && event.shiftKey) {
                        player.currentTime(player.currentTime() + 1);
                    }
                }
            }
        },
        audioKey: {
            key: only('a'),
            handler: handlers.audioKey
        },
        playbackKey: {
            key: only('r'),
            handler: handlers.playbackKey
        },
        captionSettingsKey: {
            key: only('v'),
            handler: handlers.captionSettingsKey
        },
        swapStreamsKey: {
            key: only('.'),
            handler: handlers.swapStreamsKey
        },
        lockControlsKey: {
            key: only('l'),
            handler: handlers.lockControlsKey
        },
        audioKeyAlternate: {
            key: event => event.key === 'Z' && event.ctrlKey && !event.altKey,  //Ctrl + Shift + Z
            handler: handlers.audioKey
        },
        playbackKeyAlternate: {
            key: event => event.key === '\\' && ctrlOnly(event),  //Ctrl + Backslash
            handler: handlers.playbackKey
        },
        captionSettingsKeyAlternate: {
            key: event => event.key === 'U' && event.ctrlKey && !event.altKey,  //Ctrl + Shift + U
            handler: handlers.captionSettingsKey
        },
        swapStreamsKeyAlternate: {
            key: event => event.key === '.' && ctrlOnly(event),  //Ctrl + Period
            handler: handlers.swapStreamsKey
        },
        lockControlsKeyAlternate: {
            key: event => event.key === 'L' && event.ctrlKey && !event.altKey,  //Ctrl + Shift + L
            handler: handlers.lockControlsKey
        }
    }
};

/*
 * Returns a function that listens for a key press event that does not include ctrl or alt
 */ 
function only(key) {
    return event => event.key === key && noModifiers(event);
}

/*
 * Returns true if the event does not include ctrl, alt, or shift
 */
function noModifiers(event) {
    return !event.ctrlKey && !event.altKey && !event.shiftKey;
}

/*
 * Returns true if the event includes ctrl but not alt or shift
 */
function ctrlOnly(event) {
    return event.ctrlKey && !event.altKey && !event.shiftKey;
}

/*
 * A generic function for setting up the custom keys
 * A capitalized letter means that the user should also press 'shift'
 */ 
function setupCustomKey(targetKey, plugin, functionToCall) {
    return {
        key: event => event.key === targetKey && !event.ctrlKey && !event.altKey,
        handler: (player, options, event) => {
            //checks if the plugin requested does exist before trying to get the plugin and call the corresponding function
            let playerPlugin = player[plugin];
            if (playerPlugin) {
                playerPlugin()[functionToCall]();
            }
        }
    }
}

/*
* A generic function for setting up custom keys where 'Ctrl' needs to be pressed as well
*/
function setupCustomCtrlKey(targetKey, plugin, functionToCall) {
    return {
        key: event => event.key === targetKey && event.ctrlKey && !event.altKey,
        handler: (player, options, event) => {
            //checks if the plugin requested does exist before trying to get the plugin and call the corresponding function
            let playerPlugin = player[plugin];
            if (playerPlugin) {
                playerPlugin()[functionToCall]();
            }
        }
    }
}

/*
* A generic function for setting up custom keys where 'Ctrl' needs to be pressed as well but not Shift
*/
function setupCustomCtrlOnlyKey(targetKey, plugin, functionToCall) {
    return {
        key: event => event.key === targetKey && ctrlOnly(event),
        handler: (player, options, event) => {
            //checks if the plugin requested does exist before trying to get the plugin and call the corresponding function
            let playerPlugin = player[plugin];
            if (playerPlugin) {
                playerPlugin()[functionToCall]();
            }
        }
    }
}